(() => {
  const home = document.querySelector('.bouquet-home');
  if (!home) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarsePointer = window.matchMedia('(pointer: coarse)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const threeAssetUrl = window.BouquetThreeAssetUrl || document.currentScript?.dataset.threeSrc || '';
  const cleanups = [];
  const canMove = () => !reduceMotion.matches;

  function afterInitialWork() {
    return new Promise((resolve) => {
      const schedule = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(resolve, { timeout: 1800 });
        } else {
          window.setTimeout(resolve, 700);
        }
      };

      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true });
      }
    });
  }

  function loadScriptOnce(src) {
    if (!src) return Promise.resolve(false);

    const absoluteSrc = new URL(src, window.location.href).href;
    const existing = Array.from(document.scripts).find((script) => script.src === absoluteSrc || script.getAttribute('src') === src);
    if (existing) {
      return new Promise((resolve) => {
        if (window.THREE) {
          resolve(true);
          return;
        }

        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
      });
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }

  async function ensureThree() {
    if (window.THREE) return true;
    await afterInitialWork();
    await loadScriptOnce(threeAssetUrl);
    return Boolean(window.THREE);
  }

  function initHeaderMotion() {
    const header = document.querySelector('.pill-header');
    if (!header) return;

    const update = () => {
      header.classList.toggle('is-home-scrolled', window.scrollY > 18);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', update));
  }

  function initGsapMotion() {
    const gsap = window.gsap;
    if (!gsap || !canMove()) return;

    const hero = document.querySelector('[data-home-scene="hero"]');
    const heroImage = hero?.querySelector('.bouquet-hero__image');
    const heroContent = hero?.querySelector('.bouquet-hero__content');
    const petals = gsap.utils.toArray('[data-home-petal]');

    if (window.ScrollTrigger) {
      gsap.registerPlugin(window.ScrollTrigger);
    }

    if (heroContent) {
      gsap.from(heroContent.children, {
        y: 24,
        opacity: 0,
        duration: 0.76,
        stagger: 0.08,
        ease: 'power3.out',
      });
    }

    petals.forEach((petal, index) => {
      gsap.to(petal, {
        x: index % 2 ? -18 : 16,
        y: index % 2 ? 24 : -20,
        rotate: index % 2 ? '-=7' : '+=7',
        duration: 5.8 + index * 0.55,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    });

    if (!window.ScrollTrigger) return;

    if (hero && heroImage && desktop.matches) {
      gsap.to(heroImage, {
        scale: 1.06,
        yPercent: 3,
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.8,
        },
      });
    }

    gsap.utils.toArray('.bouquet-home__heading, .bouquet-benefit, .bouquet-featured__content, .bouquet-instagram__item, .bouquet-testimonial-random__card').forEach((item, index) => {
      gsap.from(item, {
        y: 26,
        opacity: 0,
        duration: 0.66,
        delay: Math.min(index * 0.02, 0.14),
        ease: 'power3.out',
        scrollTrigger: {
          trigger: item,
          start: 'top 88%',
          once: true,
        },
      });
    });
  }

  function initTiltCards() {
    if (!canMove() || coarsePointer.matches) return;

    document.querySelectorAll('[data-home-tilt]').forEach((card) => {
      const reset = () => {
        card.classList.remove('is-tilting');
        card.style.setProperty('--home-tilt-x', '0deg');
        card.style.setProperty('--home-tilt-y', '0deg');
      };

      const move = (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.classList.add('is-tilting');
        card.style.setProperty('--home-tilt-x', `${x * 6}deg`);
        card.style.setProperty('--home-tilt-y', `${y * -5}deg`);
      };

      card.addEventListener('pointermove', move);
      card.addEventListener('pointerleave', reset);
      card.addEventListener('blur', reset, true);
      cleanups.push(() => {
        card.removeEventListener('pointermove', move);
        card.removeEventListener('pointerleave', reset);
        card.removeEventListener('blur', reset, true);
      });
    });
  }

  async function initThreePetals() {
    if (!canMove() || coarsePointer.matches || !desktop.matches) return;

    const container = document.querySelector('[data-home-three-petals]');
    if (!container) return;

    const ready = await ensureThree();
    if (!ready) return;

    const hero = document.querySelector('[data-home-scene="hero"]');
    const containerRect = container.getBoundingClientRect();
    const heroRect = hero?.getBoundingClientRect();
    const width = container.clientWidth || Math.round(containerRect.width) || window.innerWidth;
    const height = container.clientHeight || Math.round(containerRect.height) || Math.round(heroRect?.height || 0) || Math.min(window.innerHeight, 760);
    if (!width || !height) return;

    const THREE = window.THREE;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 20);
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false,
      powerPreference: 'low-power',
      preserveDrawingBuffer: true,
    });

    camera.position.z = 4.8;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setSize(width, height);
    renderer.domElement.setAttribute('aria-hidden', 'true');
    container.appendChild(renderer.domElement);

    const geometry = new THREE.CircleGeometry(0.05, 18);
    const palette = [0xffd8df, 0xfda4af, 0xe11d48, 0xffeef2];
    const petals = Array.from({ length: 22 }, (_, index) => {
      const material = new THREE.MeshBasicMaterial({
        color: palette[index % palette.length],
        transparent: true,
        opacity: 0.35 + (index % 4) * 0.08,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      const baseX = (Math.random() - 0.5) * 6.4;
      const baseY = (Math.random() - 0.5) * 3.6;
      const baseZ = (Math.random() - 0.5) * 1.4;
      mesh.position.set(baseX, baseY, baseZ);
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      mesh.scale.set(0.55 + Math.random() * 0.5, 1.05 + Math.random() * 0.8, 1);
      scene.add(mesh);
      return {
        mesh,
        baseX,
        baseY,
        speed: 0.18 + Math.random() * 0.2,
        drift: 0.18 + Math.random() * 0.34,
      };
    });

    let frame = 0;
    let disposed = false;
    const clock = new THREE.Clock();

    const animate = () => {
      if (disposed) return;
      frame = window.requestAnimationFrame(animate);
      if (document.hidden) return;

      const elapsed = clock.getElapsedTime();
      petals.forEach((petal, index) => {
        const mesh = petal.mesh;
        mesh.position.x = petal.baseX + Math.cos(elapsed * petal.speed + index) * petal.drift;
        mesh.position.y = petal.baseY + Math.sin(elapsed * petal.speed + index) * petal.drift;
        mesh.rotation.x += 0.0025 + index * 0.00002;
        mesh.rotation.y += 0.003 + index * 0.00003;
        mesh.rotation.z += 0.0018;
      });

      renderer.render(scene, camera);
    };

    const resize = () => {
      const nextRect = container.getBoundingClientRect();
      const nextHeroRect = hero?.getBoundingClientRect();
      const nextWidth = container.clientWidth || Math.round(nextRect.width) || window.innerWidth;
      const nextHeight = container.clientHeight || Math.round(nextRect.height) || Math.round(nextHeroRect?.height || 0) || Math.min(window.innerHeight, 760);
      if (!nextWidth || !nextHeight) return;
      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(nextWidth, nextHeight);
    };

    window.addEventListener('resize', resize);
    animate();

    cleanups.push(() => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      geometry.dispose();
      petals.forEach((petal) => {
        petal.mesh.material.dispose();
        scene.remove(petal.mesh);
      });
      renderer.dispose();
      renderer.domElement.remove();
    });
  }

  initHeaderMotion();
  if (!window.BouquetAnimations?.handlesHomeMotion) {
    initGsapMotion();
  }
  initTiltCards();
  initThreePetals();

  window.addEventListener('pagehide', () => {
    while (cleanups.length) cleanups.pop()();
  });
})();
